/**
 * Variacao contra o periodo anterior: glifo + texto, nunca so cor.
 *
 * Decisoes nao obvias:
 *  1. O glifo (triangulo) e decorativo e o texto diz "alta de 12,3%": quem
 *     nao distingue verde de vermelho, e o leitor de tela, recebem a mesma
 *     informacao.
 *  2. Medida em bps (taxa) nao mostra variacao relativa: "+15% na taxa" e
 *     ambiguo (15 pontos ou 15% de 20%?). Mostra "de 18,0% para 21,1%".
 *  3. `sentido` decide a cor. Comissao e "neutro": ela sobe junto com o
 *     recuperado e nao e custo (decisao do dono, 24/09/2026).
 *  4. Sem base (janela "desde o inicio" ou anterior zero) e um estado escrito,
 *     nao um zero: "sem base anterior" ou "+12 (antes: 0)".
 */

import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../../lib/format';
import { variar } from '../lib/comparacao';

export type TipoDeMedida = 'dinheiro' | 'contagem' | 'bps';
export type Sentido = 'maior_melhor' | 'neutro';

function formatarAbsoluto(valor: number, tipo: TipoDeMedida): string {
  const sinal = valor > 0 ? '+' : valor < 0 ? '−' : '';
  const abs = Math.abs(valor);
  return `${sinal}${tipo === 'dinheiro' ? formatBRLFromCents(abs) : formatCount(abs)}`;
}

export function Delta({
  atual,
  anterior,
  tipo,
  sentido = 'maior_melhor',
  className = '',
}: {
  readonly atual: number;
  /** `null` quando a janela nao tem periodo anterior. */
  readonly anterior: number | null;
  readonly tipo: TipoDeMedida;
  readonly sentido?: Sentido;
  readonly className?: string;
}): JSX.Element {
  const variacao = variar(atual, anterior);
  const base = `inline-flex flex-wrap items-center gap-1 text-xs tabular-nums ${className}`;
  const titulo = 'Comparado com o período anterior de mesmo tamanho';

  if (variacao.direcao === 'sem_base') {
    return (
      <span className={`${base} text-ink-subtle`} title={titulo}>
        sem base anterior
      </span>
    );
  }

  if (variacao.direcao === 'igual') {
    return (
      <span className={`${base} text-ink-subtle`} title={titulo}>
        <span aria-hidden="true">=</span> igual ao anterior
      </span>
    );
  }

  const alta = variacao.direcao === 'alta';
  const cor = sentido === 'neutro' ? 'text-ink-muted' : alta ? 'text-status-good' : 'text-status-critical';
  const glifo = alta ? '▲' : '▼';
  const palavra = alta ? 'alta' : 'queda';

  if (tipo === 'bps') {
    return (
      <span className={`${base} ${cor}`} title={titulo}>
        <span aria-hidden="true">{glifo}</span>
        <span className="sr-only">{palavra}:</span>
        <span>
          de {formatPercentFromBps(anterior ?? 0)} para {formatPercentFromBps(atual)}
        </span>
      </span>
    );
  }

  return (
    <span className={`${base} ${cor}`} title={titulo}>
      <span aria-hidden="true">{glifo}</span>
      <span className="sr-only">{palavra} de</span>
      {variacao.relativaBps === null ? (
        <span>
          {formatarAbsoluto(variacao.absoluta, tipo)} <span className="text-ink-subtle">(antes: 0)</span>
        </span>
      ) : (
        <span>
          {formatPercentFromBps(Math.abs(variacao.relativaBps))} <span className="text-ink-subtle">({formatarAbsoluto(variacao.absoluta, tipo)})</span>
        </span>
      )}
    </span>
  );
}
